import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { unwrapArray } from '../../lib/api-envelope';
import { classifyLoadFailure, retryUnlessRefused } from '../../lib/load-failure';
import { blurOnWheel } from '../../lib/number-input';
import {
  CADENCE_SOURCE_LABELS,
  CADENCE_TRIGGERS,
  describePolicy,
  policyBody,
  policyDraftError,
  type PolicyDraft,
} from '../../lib/service-policies';

/**
 * The asset's effective inspection cadence and where it came from (FE-5),
 * read from GET /equipment-types/service-policies/resolved — the same resolver
 * the maintenance agent uses, so this line and the agent cannot disagree.
 *
 *   asset_override → "set for this asset"   (a policy row with equipment_id)
 *   type_policy    → "from the equipment type"
 *   default        → "FieldIQ default"
 *
 * "Override for this asset" creates (or edits / removes) that asset's own
 * cadence row. An asset whose type has no inspection form has no cadence; the
 * resolver says so with a null cadence and this line says nothing.
 */
interface Resolved {
  equipmentId: string;
  equipmentTypeId: string | null;
  inspectionFormType: string | null;
  cadence: {
    triggerType: string;
    intervalValue: number | null;
    formType: string | null;
    name: string;
    policyId: string | null;
    source: string;
  } | null;
}

const inputCls = 'bg-fieldiq-dark border border-fieldiq-border rounded-lg px-2 py-1.5 text-white text-sm';

export default function InspectionCadenceLine({ equipmentId, canWrite }: { equipmentId: string; canWrite: boolean }) {
  const queryClient = useQueryClient();
  const query = useQuery<Resolved | null>({
    queryKey: ['service-policies-resolved', equipmentId],
    queryFn: () =>
      api.equipmentTypes.servicePolicies.resolved([equipmentId])
        .then((r) => unwrapArray<Resolved>(r.data).find((x) => x.equipmentId === equipmentId) ?? null),
    retry: retryUnlessRefused(),
  });
  const [draft, setDraft] = useState<PolicyDraft | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['service-policies-resolved'] });
    queryClient.invalidateQueries({ queryKey: ['service-policies'] });
  };
  const r = query.data;
  const isOverride = r?.cadence?.source === 'asset_override';

  const save = useMutation({
    mutationFn: (d: PolicyDraft) => {
      const typeId = r!.equipmentTypeId!;
      return isOverride && r!.cadence!.policyId
        ? api.equipmentTypes.servicePolicies.update(typeId, r!.cadence!.policyId, policyBody(d))
        : api.equipmentTypes.servicePolicies.create(typeId, policyBody(d, {
          form_type: r!.inspectionFormType, equipment_id: equipmentId,
        }));
    },
    onSuccess: () => { setDraft(null); setError(null); refresh(); },
    onError: (err: any) => setError(err?.response?.data?.error || 'Failed to save the override'),
  });
  const remove = useMutation({
    mutationFn: () => api.equipmentTypes.servicePolicies.remove(r!.equipmentTypeId!, r!.cadence!.policyId!),
    onSuccess: refresh,
    onError: (err: any) => setError(err?.response?.data?.error || 'Failed to remove the override'),
  });

  // A failed read says nothing rather than guessing a cadence; a refusal is
  // simply not this reader's to see. Never a default in place of an unknown.
  if (query.isLoading || classifyLoadFailure(query.error)) return null;
  if (!r || !r.cadence) return null;

  const c = r.cadence;
  const submit = () => {
    if (!draft) return;
    const problem = policyDraftError(draft);
    if (problem) { setError(problem); return; }
    save.mutate(draft);
  };

  return (
    <div className="py-2 border-b border-fieldiq-border" data-testid="inspection-cadence">
      <div className="flex items-center justify-between gap-2">
        <span className="text-fieldiq-text text-sm">Inspection Cadence</span>
        <span className="text-sm text-white text-right">
          {describePolicy({ kind: 'inspection_cadence', triggerType: c.triggerType, intervalValue: c.intervalValue })}
          <span className="text-fieldiq-muted" data-testid="cadence-source"> · {CADENCE_SOURCE_LABELS[c.source] ?? c.source}</span>
        </span>
      </div>
      {canWrite && r.equipmentTypeId && !draft && (
        <div className="flex justify-end gap-3 mt-1">
          <button
            className="text-xs text-fieldiq-accent hover:underline"
            onClick={() => {
              setError(null);
              setDraft({
                kind: 'inspection_cadence',
                trigger_type: isOverride ? c.triggerType : 'calendar_days',
                interval: isOverride && c.intervalValue != null ? String(c.intervalValue) : '',
                name: isOverride ? c.name : '',
              });
            }}
          >
            {isOverride ? 'Edit override' : 'Override for this asset'}
          </button>
          {isOverride && (
            <button
              className="text-xs text-red-400 hover:text-red-300"
              onClick={() => { if (window.confirm('Remove this asset’s own cadence? It goes back to its type.')) remove.mutate(); }}
            >
              Remove override
            </button>
          )}
        </div>
      )}
      {draft && (
        <div role="group" aria-label="Asset cadence override" className="flex flex-wrap items-center justify-end gap-2 mt-2">
          <input
            aria-label="Override name"
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            placeholder="e.g. Heavy-use unit"
            className={`${inputCls} w-40`}
          />
          <select
            aria-label="Cadence"
            value={draft.trigger_type}
            onChange={(e) => setDraft({ ...draft, trigger_type: e.target.value })}
            className={inputCls}
          >
            {CADENCE_TRIGGERS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          {draft.trigger_type !== 'per_job' && (
            <input
              aria-label="N"
              type="number"
              min={1}
              step={1}
              onWheel={blurOnWheel}
              value={draft.interval}
              onChange={(e) => setDraft({ ...draft, interval: e.target.value })}
              placeholder="N"
              className={`${inputCls} w-16`}
            />
          )}
          <button onClick={submit} disabled={save.isPending} className="px-3 py-1.5 text-xs font-semibold bg-fieldiq-accent text-white rounded-lg disabled:opacity-50">Save</button>
          <button onClick={() => { setDraft(null); setError(null); }} className="px-2 py-1.5 text-xs text-fieldiq-text">Cancel</button>
        </div>
      )}
      {error && <p className="text-xs text-red-400 mt-1 text-right">{error}</p>}
    </div>
  );
}
